import React from 'react';
import PropTypes from 'prop-types';

import { StudyItem } from '../StudyItem';
import { StudyBrowserSort } from '../StudyBrowserSort';
import { StudyBrowserViewOptions } from '../StudyBrowserViewOptions';
import { ScrollArea } from '../ScrollArea';
import { getClientName } from '../../../../../constants/baseurl';

const noop = () => { };

const StudyBrowser = ({
  displaySets,
  tabs,
  activeTabName,
  expandedStudyInstanceUIDs,
  onClickTab = noop,
  onClickStudy = noop,
  onClickThumbnail = noop,
  onDoubleClickThumbnail = noop,
  onClickUntrack = noop,
  activeDisplaySetInstanceUIDs,
  servicesManager,
  showSettings,
  viewPresets,
  ThumbnailMenuItems,
  StudyMenuItems,
  patientInfo
}: withAppTypes) => {
  const getTabContent = () => {
    const tabData = tabs.find(tab => tab.name === activeTabName);
    const viewPreset = viewPresets
      ? viewPresets.filter(preset => preset.selected)[0]?.id
      : 'thumbnails';
    return tabData?.studies?.map(
      ({ studyInstanceUid, date, description, numInstances, modalities, displaySets }) => {
        const isExpanded = expandedStudyInstanceUIDs.includes(studyInstanceUid);
        return (
          <React.Fragment key={studyInstanceUid}>
            <StudyItem
              date={date}
              description={description}
              numInstances={numInstances}
              isExpanded={isExpanded}
              displaySets={displaySets}
              modalities={modalities}
              isActive={isExpanded}
              onClick={() => onClickStudy(studyInstanceUid)}
              onClickThumbnail={onClickThumbnail}
              onDoubleClickThumbnail={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              data-cy="thumbnail-list"
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
              StudyMenuItems={StudyMenuItems}
              StudyInstanceUID={studyInstanceUid}
            />
          </React.Fragment>
        );
      }
    );
  };

  const onChangeViewer = viewerValue => {
    const { pathname, search } = window.location;
    const defaultValue = pathname.includes('petct') ? 'petct' : 'viewer';
    const newPathname = pathname.replace(defaultValue, viewerValue);
    window.location.href = newPathname + search;
  };

  const getViewerChange = () => {
    const hasPTCT = displaySets?.some(study => {
      if (study.modality === 'PT') {
        return displaySets.some(
          other => other.modality === 'CT' && other.studyInstanceUid === study.studyInstanceUid
        );
      }
      return false;
    });

    const isRadx = window.location.href.includes('/radx/');

    const urlParams = new URLSearchParams(window.location.search);
    const studyInstanceUIDs = urlParams.get('StudyInstanceUIDs');

    if (!hasPTCT || isRadx || studyInstanceUIDs.includes(',')) {
      return null;
    }

    return (
      <div className="dark:bg-primary-newgray bg-primary-gray mb-2 flex flex-col rounded-lg p-2 text-sm text-black shadow-lg dark:text-white">
        <span className="mb-2 text-center font-semibold">Select Viewer</span>
        <select
          className="dark:bg-primary-newgray bg-primary-gray rounded-md border p-1 text-black dark:text-white"
          onChange={e => onChangeViewer(e.target.value)}
          defaultValue={window.location.pathname.includes('petct') ? 'petct' : 'viewer'}
        >
          <option value="viewer">Basic Viewer</option>
          <option value="petct">PET CT Viewer</option>
        </select>
      </div>
    );
  };

  const calculateAge = dob => {
    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }

    return `${age} Year(s) Old`;
  };

  const showPriorStudies = getClientName() !== 'indus';

  return (
    <div
      className="ohif-scrollbar invisible-scrollbar bg-bkg-low flex flex-1 flex-col gap-[4px] overflow-auto"
      data-cy={'studyBrowser-panel'}
    >
      <div>
        {!showSettings && showPriorStudies && (
          <div className="dark:bg-primary-newgray bg-primary-gray mb-2 rounded-lg p-1 shadow-md">
            <div className="text-center text-sm text-black dark:text-white">Prior Studies</div>
            <div className="w-100 flex h-[48px] items-center justify-center gap-[10px] px-[8px] py-[10px]">
              <>
                <StudyBrowserViewOptions
                  tabs={tabs}
                  onSelectTab={onClickTab}
                  activeTabName={activeTabName}
                />
                <StudyBrowserSort servicesManager={servicesManager} />
              </>
            </div>
          </div>
        )}

        {patientInfo &&
          (patientInfo.PatientName || patientInfo.PatientDOB || patientInfo.PatientSex) && (
            <div className="dark:bg-primary-newgray bg-primary-gray mb-2 flex flex-col rounded-lg p-2 text-sm text-black shadow-lg dark:text-white">
              <span className="text-center font-semibold">Patient Info</span>

              {patientInfo.PatientName && (
                <span className="">
                  <span className="font-semibold">Patient Name: </span>
                  <span className="truncate">{patientInfo.PatientName}</span>
                </span>
              )}

              {patientInfo.PatientDOB && (
                <span className="">
                  <span className="font-semibold">Patient DOB: </span>
                  <span className="truncate">{patientInfo.PatientDOB}</span>
                </span>
              )}

              {patientInfo.PatientDOB && (
                <span className="">
                  <span className="font-semibold">Patient Age: </span>
                  <span className="truncate">{calculateAge(patientInfo.PatientDOB)}</span>
                </span>
              )}
            </div>
          )}
        {getViewerChange()}
        {getTabContent()}
      </div>
    </div>
  );
};

StudyBrowser.propTypes = {
  onClickTab: PropTypes.func.isRequired,
  onClickStudy: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  activeTabName: PropTypes.string.isRequired,
  expandedStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      studies: PropTypes.arrayOf(
        PropTypes.shape({
          studyInstanceUid: PropTypes.string.isRequired,
          date: PropTypes.string,
          numInstances: PropTypes.number,
          modalities: PropTypes.string,
          description: PropTypes.string,
          displaySets: PropTypes.arrayOf(
            PropTypes.shape({
              displaySetInstanceUID: PropTypes.string.isRequired,
              imageSrc: PropTypes.string,
              imageAltText: PropTypes.string,
              seriesDate: PropTypes.string,
              seriesNumber: PropTypes.any,
              numInstances: PropTypes.number,
              description: PropTypes.string,
              componentType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage'])
                .isRequired,
              isTracked: PropTypes.bool,
              /**
               * Data the thumbnail should expose to a receiving drop target. Use a matching
               * `dragData.type` to identify which targets can receive this draggable item.
               * If this is not set, drag-n-drop will be disabled for this thumbnail.
               *
               * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
               */
              dragData: PropTypes.shape({
                /** Must match the "type" a dropTarget expects */
                type: PropTypes.string.isRequired,
              }),
            })
          ),
        })
      ).isRequired,
    })
  ),
  StudyMenuItems: PropTypes.func,
};

export { StudyBrowser };
